import { useSyncStore } from './sync-store';
import {
  getPendingCount,
  getFailedMutations,
  retryFailedMutations,
  discardFailedMutation as discardMutationRow,
} from './sync-queue';
import {
  getPendingUploadCount,
  getFailedUploads,
  retryFailedUploads,
  discardFailedUpload as discardUploadRow,
} from './upload-sync';

/**
 * The bridge between the two queues and the status bar.
 *
 * Lives apart from `sync-store` deliberately — see the import note there. The
 * store stays a leaf that any component can pull in; this module knows about
 * IndexedDB and is imported only by the few places that drive a sync.
 */

/**
 * Re-read every counter the status bar renders, straight from IndexedDB.
 *
 * Called from `finally` blocks, not success paths. `performSync` now rejects
 * when the pull fails — and a failed sync is precisely when something may have
 * landed in the failed queue, so refreshing only on success would hide the red
 * bar exactly when it is needed.
 *
 * Every read below already degrades to an empty list when the offline cache is
 * sealed, so this cannot throw for that reason.
 */
export async function refreshSyncCounts(): Promise<void> {
  const [pending, uploads, failedMutations, failedUploads] = await Promise.all([
    getPendingCount(),
    getPendingUploadCount(),
    getFailedMutations(),
    getFailedUploads(),
  ]);
  const store = useSyncStore.getState();
  store.setPendingMutations(pending);
  store.setPendingUploads(uploads);
  store.setFailedDetails(failedMutations, failedUploads);
}

/** User tapped "Try again" on the failed bar. */
export async function retryFailed(): Promise<void> {
  await retryFailedMutations();
  await retryFailedUploads();
  await refreshSyncCounts();
}

export async function discardFailedMutation(clientMutationId: string): Promise<void> {
  await discardMutationRow(clientMutationId);
  await refreshSyncCounts();
}

export async function discardFailedUpload(id: string): Promise<void> {
  await discardUploadRow(id);
  await refreshSyncCounts();
}
